import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { showSnackbar } from '../../../store/slices/common.slice';
import {
  APIResponse,
  FilterConfig,
  FreeKeyObject,
  ListingResponse,
  TypeSnackbarPayload,
} from '../../../utilities/types';
import DatagridLoader from '../../elements/datagrid-loader.element';
import useAuctionBroadcastWsHook from '../../../hooks/auction-broadcast-ws.hook';
import { RootState } from '../../../store';
import {
  addBuyerToAuctionApi,
  changeBidRequestStatusApi,
  createAdminApiUrl,
  getBidRequestListApi,
  removeBidRequestApi,
} from '../../../apis/admin';
import ListFiltersWidget from '../list-filters.widget';
import CustomSwal from '../../elements/custom-swal.element';
import AutocompleteSearch from '../../elements/autocomplete-search-element';

interface BidRequestGridWidgetProps {
  auction_id: number;
  showAddBuyerForm?: boolean;
}

const BidRequestGridWidget: React.FC<BidRequestGridWidgetProps> = ({
  auction_id,
  showAddBuyerForm = true,
}) => {
  const dispatch = useDispatch();

  const { token } = useSelector((state: RootState) => state.auth);
  const { systemConfig } = useSelector((state: RootState) => state.common);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>({});
  const [newBuyers, setNewBuyers] = useState<any[]>([]);

  const pageSize = 20;

  // Simulate API call (replace with your real API)
  const fetchData = useCallback(
    async (pageNumber: number) => {
      setLoading(true);
      try {
        const payload = {
          ...filterValues,
          auction_id,
          page: pageNumber + 1,
          limit: pageSize,
        };
        const response = (await getBidRequestListApi(payload)) as APIResponse<ListingResponse>;
        if (response.status) {
          const items: any = (Array.isArray(response.data?.items) ? response.data.items : []).map(
            (item: any) => {
              return {
                ...item,
                name: item?.buyer.name,
                company_name: item?.buyer.company_name,
                mobile_number: item?.buyer.mobile_number,
              };
            }
          );
          setRows(items);
          setRowCount(response.data?.pagination?.total);
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
      setLoading(false);
    },
    [dispatch, pageSize, auction_id, filterValues]
  );

  const changeBidStatus = useCallback(
    async (payload: any) => {
      try {
        const response = (await changeBidRequestStatusApi(payload)) as APIResponse<FreeKeyObject>;
        if (response.status) {
          fetchData(0);
          CustomSwal.fire({
            icon: 'success',
            title: 'Bid Request Status Changed',
            text:
              payload.status === 'reject'
                ? `Request has been rejected. Buyer will not be able to place bid`
                : `Request has been approved. Buyer will now able to place bid`,
            target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
          });
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
    },
    [dispatch, fetchData]
  );

  const onApprove = useCallback(
    async (application_id: number) => {
      const result = await CustomSwal.fire({
        title: 'Are you sure?',
        text: `You won't be revert changes`,
        input: 'textarea',
        inputPlaceholder: 'Optional. Type your message here...',
        inputAttributes: {
          'aria-label': 'Type your message here',
          maxlength: '200',
        },
        allowOutsideClick: false,
        showCancelButton: true,
        target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
      });
      if (result.isConfirmed) {
        changeBidStatus({
          id: application_id,
          admin_comment: result.value,
          status: 'approved',
        });
      }
    },
    [changeBidStatus]
  );

  const onReject = useCallback(
    async (application_id: number) => {
      const result = await CustomSwal.fire({
        title: 'Are you sure?',
        text: `You won't be revert changes`,
        input: 'textarea',
        inputPlaceholder: 'Optional. Type your message here...',
        inputAttributes: {
          'aria-label': 'Type your message here',
          maxlength: '200',
        },
        allowOutsideClick: false,
        showCancelButton: true,
        target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
      });
      if (result.isConfirmed) {
        changeBidStatus({
          id: application_id,
          admin_comment: result.value,
          status: 'reject',
        });
      }
    },
    [changeBidStatus]
  );

  const onDelete = useCallback(
    async (bid_application_id: number) => {
      const result = await CustomSwal.fire({
        title: 'Are you sure?',
        text: `You won't be revert changes`,
        allowOutsideClick: false,
        showCancelButton: true,
        target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
      });
      if (result.isConfirmed) {
        try {
          const payload = { id: bid_application_id };
          const response = (await removeBidRequestApi(payload)) as APIResponse<FreeKeyObject>;
          if (response.status) {
            fetchData(0);
            CustomSwal.fire({
              icon: 'success',
              title: 'Done',
              text: 'Buyer remove auction',
              target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
            });
          }
        } catch (err: any) {
          const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
          if (err?.data?.message) {
            snakbarpayload.message = err?.data?.message;
          } else if (err?.statusText) {
            snakbarpayload.message = err?.statusText;
          }
          dispatch(showSnackbar(snakbarpayload));
        }
      }
    },
    [dispatch, fetchData]
  );

  const addBuyerToAuctions = useCallback(
    async (comment: string) => {
      try {
        const payload = {
          user_ids: newBuyers.map((user) => user.id),
          auction_id,
          comment,
        };
        const response = (await addBuyerToAuctionApi(payload)) as APIResponse<FreeKeyObject>;
        if (response.status) {
          setNewBuyers([]);
          fetchData(0);
          CustomSwal.fire({
            icon: 'success',
            title: 'Done',
            text: 'Buyer(s) added on this auction',
            target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
          });
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
    },
    [dispatch, newBuyers, auction_id, fetchData]
  );

  const onSelectNewBuyer = useCallback((users: any[]) => {
    setNewBuyers([...users]);
  }, []);

  const mapNewBuyersToAuction = useCallback(async () => {
    const result = await CustomSwal.fire({
      title: 'Comment',
      text: 'Any Comment?',
      input: 'textarea',
      inputPlaceholder: 'Optional. Type your message here...',
      inputAttributes: {
        'aria-label': 'Type your message here',
        maxlength: '100',
      },
      allowOutsideClick: false,
      showCancelButton: true,
      target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
    });
    if (result.isConfirmed) {
      addBuyerToAuctions(result.value);
    }
  }, [addBuyerToAuctions]);

  const renderAutoCompleteOption = useCallback((option: any) => {
    return (
      <Stack direction="column" gap={0}>
        <Typography variant="h6" fontWeight={600} fontSize={14}>
          {option.name}
        </Typography>
        <Typography component="span" fontSize={12}>
          {option.mobile_number}
        </Typography>
      </Stack>
    );
  }, []);

  useEffect(() => {
    fetchData(page);
  }, [fetchData, page]);

  useAuctionBroadcastWsHook(token, () => {
    fetchData(page);
  });

  // Columns Definition
  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'company_name', headerName: 'Company', flex: 1 },
    { field: 'mobile_number', headerName: 'Mobile Number', flex: 1 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params: any) => {
        if (params.value === 'pending') {
          return <Chip label="Pending" color="primary" variant="filled" size="small" />;
        }
        if (params.value === 'reject') {
          return <Chip label="Rejected" color="error" variant="filled" size="small" />;
        }
        if (params.value === 'approved') {
          return <Chip label="Approved" color="success" variant="filled" size="small" />;
        }
        return params.value;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: any) => {
        if (params.row.status === 'approved') {
          return (
            <IconButton color="error" onClick={() => onDelete(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          );
        }
        if (params.row.status !== 'pending') {
          return null;
        }
        return (
          <Box>
            <IconButton color="primary" onClick={() => onApprove(params.row.id)}>
              <CheckCircleIcon />
            </IconButton>
            <IconButton color="error" onClick={() => onReject(params.row.id)}>
              <CancelIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const statuses = Object.entries(systemConfig?.bid_application_statuses || {}).map((v: any) => ({
    value: v[0],
    label: v[1],
  }));

  const filterConfig: FilterConfig[] = [
    {
      name: 'buyer_name',
      label: 'Name',
      type: 'text',
    },
    {
      name: 'buyer_mobile_number',
      label: 'Mobile Number',
      type: 'text',
    },
    {
      name: 'status',
      label: 'Request Status',
      type: 'select',
      options: statuses,
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <ListFiltersWidget
          filters={filterConfig}
          onSearch={setFilterValues}
          onReset={() => setFilterValues({})}
        />
      </Box>
      {showAddBuyerForm && (
        <Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="start"
            gap={2}
          >
            <AutocompleteSearch
              value={newBuyers}
              serverSide
              optionLabel="name"
              multiple
              serverConfigs={{
                url: createAdminApiUrl('users'),
                limit: 20,
                payload: {
                  filters: { user_type: 'buyer' },
                },
              }}
              onChange={onSelectNewBuyer}
              renderOptionFn={renderAutoCompleteOption}
            />
            <Box sx={{ width: '250px' }}>
              <Button
                variant="contained"
                color="primary"
                disabled={newBuyers.length === 0}
                onClick={mapNewBuyersToAuction}
                sx={{ width: '100%' }}
              >
                Add Buyers to Auction
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
      <DataGrid
        rows={rows}
        columns={columns.map((col) => ({ ...col, sortable: false }))}
        pageSizeOptions={[pageSize]}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        rowCount={rowCount}
        onPaginationModelChange={(newModel: any) => setPage(newModel.page)}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        slots={{
          loadingOverlay: DatagridLoader,
        }}
        sx={{
          // '& .MuiDataGrid-row': {
          //   maxHeight: 'inherit !important',
          //   '--height': '75px !important',
          // },
          '& .item-img-cell': {
            display: 'flex',
          },
        }}
      />
    </Box>
  );
};

export default BidRequestGridWidget;
